import React, { useEffect, useState, useRef, useCallback } from 'react';
import './ImageModal.css';

const ImageModal = ({ images, currentIndex, onClose, onNavigate, initialRect }) => {
    const [phase, setPhase] = useState('initial'); // initial | open | closing
    const [imgNaturalSize, setImgNaturalSize] = useState(null);
    const imgRef = useRef(null);

    // Compute final rendered size (constrained to 90vw/90vh)
    const getFinalSize = useCallback(() => {
        if (!imgNaturalSize) return null;
        const maxW = window.innerWidth * 0.9;
        const maxH = window.innerHeight * 0.9;
        const ratio = imgNaturalSize.width / imgNaturalSize.height;
        let w = imgNaturalSize.width;
        let h = imgNaturalSize.height;
        if (w > maxW) { w = maxW; h = w / ratio; }
        if (h > maxH) { h = maxH; w = h * ratio; }
        return { width: w, height: h };
    }, [imgNaturalSize]);

    const getTransform = useCallback((targetPhase) => {
        if (!initialRect || !imgNaturalSize) return {};
        const final = getFinalSize();
        if (!final) return {};

        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Center of the original image
        const srcCX = initialRect.left + initialRect.width / 2;
        const srcCY = initialRect.top + initialRect.height / 2;

        // Center of screen
        const dstCX = vw / 2;
        const dstCY = vh / 2;

        const scaleX = initialRect.width / final.width;
        const scaleY = initialRect.height / final.height;
        const scale = Math.min(scaleX, scaleY);

        const dx = srcCX - dstCX;
        const dy = srcCY - dstCY;

        if (targetPhase === 'initial') {
            return {
                transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(${scale})`,
                transition: 'none',
            };
        }
        if (targetPhase === 'closing') {
            return {
                transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(${scale})`,
                transition: 'transform 0.3s ease-out',
            };
        }
        // open
        return {
            transform: 'translate(-50%, -50%) scale(1)',
            transition: 'transform 0.3s ease-out',
        };
    }, [initialRect, imgNaturalSize, getFinalSize]);

    const handleImgLoad = () => {
        const img = imgRef.current;
        if (!img) return;
        setImgNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    };

    // Once we have natural size, kick off open animation
    useEffect(() => {
        if (!imgNaturalSize) return;
        // next frame: transition to open
        const raf = requestAnimationFrame(() => {
            setPhase('open');
        });
        return () => cancelAnimationFrame(raf);
    }, [imgNaturalSize]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') handleClose();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };
        document.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = 'unset';
        };
    }, [currentIndex]);

    const handleClose = () => {
        setPhase('closing');
        setTimeout(() => onClose(), 300);
    };

    const handlePrev = () => { if (currentIndex > 0) onNavigate(currentIndex - 1); };
    const handleNext = () => { if (currentIndex < images.length - 1) onNavigate(currentIndex + 1); };

    const final = getFinalSize();
    const transformStyle = getTransform(phase);

    const imageStyle = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: final ? `${final.width}px` : 'auto',
        height: final ? `${final.height}px` : 'auto',
        maxWidth: '90vw',
        maxHeight: '90vh',
        objectFit: 'contain',
        transformOrigin: 'center center',
        visibility: imgNaturalSize ? 'visible' : 'hidden',
        ...transformStyle,
    };

    return (
        <div
            className={`image-modal ${phase === 'closing' ? 'closing' : ''}`}
            onClick={handleClose}
        >
            <button className="image-modal__close" onClick={handleClose}>×</button>

            {images.length > 1 && (
                <>
                    {currentIndex > 0 && (
                        <button
                            className="image-modal__nav image-modal__nav--prev"
                            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                        >
                            ‹
                        </button>
                    )}
                    {currentIndex < images.length - 1 && (
                        <button
                            className="image-modal__nav image-modal__nav--next"
                            onClick={(e) => { e.stopPropagation(); handleNext(); }}
                        >
                            ›
                        </button>
                    )}
                    <div className="image-modal__counter">
                        {currentIndex + 1} / {images.length}
                    </div>
                </>
            )}

            <img
                ref={imgRef}
                src={images[currentIndex]}
                alt=""
                className="image-modal__image"
                style={imageStyle}
                onLoad={handleImgLoad}
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );
};

export default ImageModal;